import {
    EMPTY_OBJECT_PATH,
    getDefaultRequiredValue,
} from "components/Form/components/SchemaForm/SchemaFormService";
import {
    SchemaFormArrayItemIdProvider,
    useSchemaFormArrayItemIdRegistry,
} from "components/Form/components/SchemaForm/SchemaFormArrayItemIds";
import { SchemaFormFragment } from "components/Form/components/SchemaForm/fragments/SchemaFormFragment";
import { SubmitLock } from "components/Form/components/SubmitLock/SubmitLock";
import { useContext, useMemo, useState } from "react";
import { RouterContext } from "utils/RouterManager/RouterManager";
import { MessageSubmitWaitingHint } from "../MessageSubmitWaitingHint";
import { useMessageSubmitLock } from "../useMessageSubmitLock";
import "./MessageSchemaForm.scss";

interface MessageSchemaFormProps {
    /** The canonical schema-form dialect message body (a top-level `form-*` element node). */
    body: any;
    /** The message's current stored response (prefill / already-answered values). */
    response?: { [key: string]: any };
    /** Whether the message has already been answered (renders read-only). */
    submitted: boolean;
    /**
     * Submit sink. Called with the full response object (existing form data merged
     * with {<chosenAction>: true}) when a `form-submit` action button is pressed.
     * Return the update promise so the form stays locked while it is in flight.
     */
    onSubmit: (response: { [key: string]: any }) => unknown;
}

/**
 * Renders an interactive `system.message()` whose body uses the canonical
 * schema-form dialect (a top-level `form-*` element node), using the same
 * {@link SchemaFormFragment} renderer as input_schema forms. Legacy-dialect
 * message bodies (`{type:object, properties:{...}}` with bare element names)
 * keep using MessageEditFields.
 *
 * The dialect's `form-submit` element (operator-task-schema-form-add-form-submit-
 * action-element) renders as an action button whose property NAME is the chosen
 * action; clicking it writes `{<name>: true}` into the form data and invokes
 * `onSubmitAction(name)`. We build the submitted response deterministically from
 * the callback argument (not the just-set async form state) so the pressed
 * button's key is always recorded — mirroring the legacy "which key is true in
 * the response" dispatch, so interactive.wrapper-style flows need no change.
 */
export const MessageSchemaForm = ({
    body,
    response,
    submitted,
    onSubmit,
}: MessageSchemaFormProps) => {
    const ajv = useContext(RouterContext).ajv;
    const arrayItemIdRegistry = useSchemaFormArrayItemIdRegistry();
    const { pendingAction, locked, showWaitingHint, runSubmit } = useMessageSubmitLock(submitted);

    // Seed field defaults so an untouched form still submits its shown values.
    // A message `response` is stored verbatim (no server-side default coercion,
    // unlike input_schema forms), so — unlike the schema-form engine's
    // required-only default handling — we must ALSO seed NON-required field
    // defaults here; otherwise a default that is visibly rendered (e.g. a
    // form-boolean defaulting on) is dropped from the response unless the user
    // touches the field. `form-submit` leaves carry no default, so they stay
    // unseeded — only the pressed button's key is written into the response.
    const seededDefaults = useMemo(() => {
        const base: { [key: string]: any } = getDefaultRequiredValue(body, body, null) ?? {};
        const properties = body?.properties;
        if (properties && typeof properties === "object") {
            for (const [key, subSchema] of Object.entries(properties)) {
                if (key in base) {
                    continue;
                }
                const fieldDefault = getDefaultRequiredValue(body, subSchema, null);
                if (fieldDefault !== undefined) {
                    base[key] = fieldDefault;
                }
            }
        }
        return base;
    }, [body]);
    const [formData, setFormData] = useState<{ [key: string]: any }>(() => ({
        ...seededDefaults,
        ...(response ?? {}),
    }));

    return (
        <div className="MessageSchemaForm">
            <SubmitLock locked={locked}>
                <SchemaFormArrayItemIdProvider value={arrayItemIdRegistry}>
                    <SchemaFormFragment
                        isRoot
                        isReadOnly={submitted}
                        isRecordDisabled={submitted}
                        schema={body}
                        schemaPath={EMPTY_OBJECT_PATH}
                        formData={formData}
                        dataPath={EMPTY_OBJECT_PATH}
                        setData={setFormData}
                        showLabel
                        // A message is a fill-once answer form, not a schema editor,
                        // so suppress the per-field unset / reset-to-default / remove
                        // action buttons that SchemaFormFragment renders by default.
                        hideActionButtons
                        ajv={ajv}
                        onSubmitAction={(actionName) =>
                            runSubmit(actionName, () =>
                                onSubmit({ ...formData, [actionName]: true }),
                            )
                        }
                        pendingSubmitAction={pendingAction}
                    />
                </SchemaFormArrayItemIdProvider>
            </SubmitLock>
            <MessageSubmitWaitingHint visible={showWaitingHint} />
        </div>
    );
};
